import { useFragment } from "@/generated";
import { SetSlideTagsDocument, SlideFieldsFragmentDoc } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for bulk-replacing the tags of multiple slides in a single mutation.
 *
 * Tags are non-destructive (slideData and knowledgeArea are left untouched) and are synced to
 * linked copies. The backend applies every update inside one transaction, so the batch is
 * atomic. Apollo's normalized cache picks up the new tagIds from the returned slides; unlike
 * skill, no query is filtered by tag, so there are no cached lists to reconcile.
 *
 * @returns An object containing:
 *   - setSlideTags: Async function taking slide IDs and tag IDs, returns the updated slides.
 *   - loading: Boolean indicating if the mutation is in progress
 */
export function useSetSlideTags() {
  const [setSlideTagsMutation, { loading }] = useMutation(SetSlideTagsDocument);

  const setSlideTags = async (slideIds: string[], tagIds: string[]) => {
    return withToast(
      async () => {
        const result = await setSlideTagsMutation({ variables: { slideIds, tagIds } });

        const maskedSlides = result.data?.setSlideTags ?? [];
        return maskedSlides.map((slide) => useFragment(SlideFieldsFragmentDoc, slide));
      },
      {
        loading: "Updating slide tags...",
        success: `Successfully updated ${slideIds.length} slide${slideIds.length === 1 ? "" : "s"}`,
        error: "Failed to update slide tags. Please try again.",
      },
      "setSlideTags"
    );
  };

  return {
    setSlideTags,
    loading,
  };
}
